import React, { useMemo, useState } from 'react';
import moment, { parseRomeDateTime } from '../config/romeTime';
import StudentPicker, { isOfflineEmail } from './StudentPicker';

const DURATIONS = [45, 60, 90];
const REPEAT_OPTIONS = [4, 8, 12, 16];

// Форма бронирования урока от имени ученика. Используется и во всплывающем окне
// по клику на ячейку календаря, и в свёрнутом блоке «Урок вручную».
// onSubmit({ student, start: Date, duration, repeatCount, remind }) → Promise<boolean>
export default function LessonForm({ students, initialStart, findConflicts, onSubmit, onCancel, submitLabel = 'Забронировать', autoFocus = true }) {
  const init = initialStart ? moment(initialStart) : null;
  const [student, setStudent] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [date, setDate] = useState(init ? init.format('YYYY-MM-DD') : '');
  const [time, setTime] = useState(init ? init.format('HH:mm') : '');
  const [duration, setDuration] = useState(60);
  const [repeat, setRepeat] = useState(false);
  const [repeatCount, setRepeatCount] = useState(4);
  const [remind, setRemind] = useState(true);
  const [saving, setSaving] = useState(false);

  const start = date && time ? parseRomeDateTime(date, time) : null;
  const end = start ? new Date(start.getTime() + duration * 60000) : null;

  const email = student ? (student.isNew ? (student.email || newEmail.trim().toLowerCase()) : student.email) : '';
  const hasRealEmail = !!email && email.includes('@') && !isOfflineEmail(email);
  const newEmailInvalid = student?.isNew && !student.email && newEmail.trim() && !newEmail.includes('@');

  const conflicts = useMemo(
    () => (start && findConflicts ? findConflicts(start, end, repeat ? repeatCount : 0) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, time, duration, repeat, repeatCount, findConflicts]
  );

  const canSubmit = !!student && !!start && !saving && !newEmailInvalid && conflicts.length === 0 && (student.isNew ? !!(student.name || email) : true);

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    const ok = await onSubmit({
      student: { ...student, email: student.isNew ? email : student.email },
      start,
      duration,
      repeatCount: repeat ? repeatCount : 0,
      remind: remind && hasRealEmail,
    });
    setSaving(false);
    if (ok) {
      setStudent(null);
      setNewEmail('');
      setRepeat(false);
    }
  };

  return (
    <form className="adm-form" onSubmit={submit}>
      <div className="adm-field">
        <label className="adm-label">Ученик</label>
        <StudentPicker students={students} value={student} onChange={setStudent} autoFocus={autoFocus} />
      </div>

      {student?.isNew && (
        <div className="adm-field">
          {student.email ? (
            <>
              <label className="adm-label">Имя</label>
              <input className="adm-input" type="text" placeholder="Как подписать в календаре" value={student.name} onChange={(e) => setStudent({ ...student, name: e.target.value })} />
            </>
          ) : (
            <>
              <label className="adm-label">Email <span className="adm-optional">необязательно</span></label>
              <input className={'adm-input' + (newEmailInvalid ? ' invalid' : '')} type="email" placeholder="для напоминаний и входа в личный кабинет" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              <div className="adm-hint">Без email урок тоже сохранится — ученик просто не получит письмо-напоминание.</div>
            </>
          )}
        </div>
      )}

      <div className="adm-row">
        <div className="adm-field">
          <label className="adm-label">Дата</label>
          <input className="adm-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>
        <div className="adm-field">
          <label className="adm-label">Начало · Rome</label>
          <input className="adm-input" type="time" step="900" value={time} onChange={(e) => setTime(e.target.value)} required />
        </div>
      </div>

      <div className="adm-field">
        <label className="adm-label">Длительность</label>
        <div className="adm-segment">
          {DURATIONS.map((d) => (
            <button type="button" key={d} className={duration === d ? 'on' : ''} onClick={() => setDuration(d)}>{d} мин</button>
          ))}
        </div>
        {start && <div className="adm-hint">{moment(start).format('HH:mm')} – {moment(end).format('HH:mm')}</div>}
      </div>

      <label className="adm-check">
        <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} />
        <span>Повторять каждую неделю</span>
        {repeat && (
          <select className="adm-input adm-input-sm" value={repeatCount} onChange={(e) => setRepeatCount(Number(e.target.value))}>
            {REPEAT_OPTIONS.map((n) => <option key={n} value={n}>ещё {n} нед.</option>)}
          </select>
        )}
      </label>

      {hasRealEmail && (
        <label className="adm-check">
          <input type="checkbox" checked={remind} onChange={(e) => setRemind(e.target.checked)} />
          <span>Напомнить ученику письмом за 1,5 часа</span>
        </label>
      )}

      {conflicts.length > 0 && (
        <div className="adm-warn">
          ⚠️ Пересекается: {conflicts.slice(0, 3).join(' · ')}{conflicts.length > 3 ? ` и ещё ${conflicts.length - 3}` : ''}
        </div>
      )}

      <div className="adm-actions">
        {onCancel && <button type="button" className="adm-btn adm-btn-ghost" onClick={onCancel}>Отмена</button>}
        <button type="submit" className="adm-btn adm-btn-primary" disabled={!canSubmit}>
          {saving ? 'Сохраняю…' : `📚 ${submitLabel}${repeat ? ` (${repeatCount + 1})` : ''}`}
        </button>
      </div>
    </form>
  );
}
